import { findLast } from 'lodash';
import { App, View } from '@gin-betting/core';

import type { MatchPartInfo } from 'types';
import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';

const TENNIS_HIDDEN_SCORES_TYPES_RESULT_PAGE: App.ScoreTypes[] = [
    App.ScoreTypes.TYPE_GOLDEN_SET,
    App.ScoreTypes.TYPE_GAME_POINTS,
    App.ScoreTypes.TYPE_TIE_BREAK,
];

const footballFilter = (
    scores: View.ScoreInfo[],
    matchPartInfo: MatchPartInfo
) => {
    switch (true) {
        case matchPartInfo.type === App.MetaTypes.TYPE_PERIOD_1ST_HALF: {
            return scores.filter(s =>
                [App.ScoreTypes.TYPE_TOTAL].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_PERIOD_2ND_HALF: {
            return scores.filter(s =>
                [
                    App.ScoreTypes.TYPE_TOTAL,
                    App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
                ].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_PERIOD_1ST_EXTRA: {
            return scores.filter(s =>
                [
                    App.ScoreTypes.TYPE_TOTAL,
                    App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
                    App.ScoreTypes.TYPE_PERIOD_2ND_HALF,
                ].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_PERIOD_2ND_EXTRA: {
            return scores.filter(s =>
                [
                    App.ScoreTypes.TYPE_TOTAL,
                    App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
                    App.ScoreTypes.TYPE_PERIOD_2ND_HALF,
                    App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA,
                ].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_PERIOD_PENALTIES: {
            return scores.filter(s =>
                [
                    App.ScoreTypes.TYPE_TOTAL,
                    App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
                    App.ScoreTypes.TYPE_PERIOD_2ND_HALF,
                    App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA,
                    App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA,
                ].includes(s.type)
            );
        }

        default: {
            return scores;
        }
    }
};

const iceHockeyFilter = (
    scores: View.ScoreInfo[],
    matchPartInfo: MatchPartInfo
) => {
    switch (true) {
        case matchPartInfo.type === App.MetaTypes.TYPE_REGULAR &&
            matchPartInfo.indexNumber === 1: {
            return scores.filter(s =>
                [App.ScoreTypes.TYPE_TOTAL].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_REGULAR &&
            matchPartInfo.indexNumber === 2: {
            return scores.filter(
                s =>
                    s.type === App.ScoreTypes.TYPE_TOTAL ||
                    (s.type === App.ScoreTypes.TYPE_PERIOD_REGULAR &&
                        s.indexNumber === 1)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_REGULAR &&
            matchPartInfo.indexNumber === 3: {
            return scores.filter(
                s =>
                    s.type === App.ScoreTypes.TYPE_TOTAL ||
                    (s.type === App.ScoreTypes.TYPE_PERIOD_REGULAR &&
                        [1, 2].includes(s.indexNumber))
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_OVERTIME: {
            return scores.filter(s =>
                [
                    App.ScoreTypes.TYPE_TOTAL,
                    App.ScoreTypes.TYPE_PERIOD_OVERTIME,
                    App.ScoreTypes.TYPE_PERIOD_REGULAR,
                ].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_BULLETS: {
            return scores.filter(s =>
                [
                    App.ScoreTypes.TYPE_TOTAL,
                    App.ScoreTypes.TYPE_PERIOD_REGULAR,
                ].includes(s.type)
            );
        }

        default: {
            return scores;
        }
    }
};

const basketballFilter = (
    scores: View.ScoreInfo[],
    matchPartInfo: MatchPartInfo
) => {
    switch (true) {
        case matchPartInfo.type === App.MetaTypes.TYPE_QUARTER &&
            matchPartInfo.indexNumber === 1: {
            return scores.filter(s =>
                [App.ScoreTypes.TYPE_TOTAL].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_OVERTIME: {
            const lastOT = findLast(
                scores,
                s => s.type === App.ScoreTypes.TYPE_PERIOD_OVERTIME
            );

            if (lastOT) {
                return scores.filter(
                    s =>
                        !(
                            s.type === App.ScoreTypes.TYPE_PERIOD_OVERTIME &&
                            s.indexNumber !== lastOT.indexNumber
                        )
                );
            } else {
                return scores;
            }
        }

        default: {
            return scores;
        }
    }
};

const tennisFilter = (
    scores: View.ScoreInfo[],
    { type, indexNumber, status }: MatchPartInfo
) => {
    const filteredScores = LIVE_SPORTEVENT_STATUSES.includes(status)
        ? scores
        : scores.filter(
              s => !TENNIS_HIDDEN_SCORES_TYPES_RESULT_PAGE.includes(s.type)
          );

    switch (true) {
        case type === App.MetaTypes.TYPE_SET: {
            const isTbForCurrentSetExists = filteredScores.find(
                s =>
                    s.type === App.ScoreTypes.TYPE_TIE_BREAK &&
                    s.indexNumber === indexNumber
            );

            return filteredScores.filter(s => {
                return !(
                    (s.type === App.ScoreTypes.TYPE_TIE_BREAK &&
                        s.indexNumber !== indexNumber) ||
                    (s.type === App.ScoreTypes.TYPE_GAME_POINTS &&
                        isTbForCurrentSetExists)
                );
            });
        }

        default: {
            return filteredScores;
        }
    }
};

const filterScoresBySportAndMatchType = (
    sportId: string,
    matchPartInfo: MatchPartInfo,
    scores: View.ScoreInfo[]
): View.ScoreInfo[] => {
    if (!matchPartInfo.type || !matchPartInfo.indexNumber) {
        return scores;
    }

    switch (sportId) {
        case 'football':
        case 'esports_fifa':
            return footballFilter(scores, matchPartInfo);

        case 'ice_hockey':
            return iceHockeyFilter(scores, matchPartInfo);

        case 'basketball':
            return basketballFilter(scores, matchPartInfo);

        case 'tennis':
        case 'esports_tennis':
            return tennisFilter(scores, matchPartInfo);

        default: {
            return scores;
        }
    }
};

export default filterScoresBySportAndMatchType;
